import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { CtaBand } from "@/components/CtaBand";
import { VIDEO, POSTER } from "@/lib/images";
import { FORM_ENDPOINT } from "@/data/company";

const Label = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
    {children}
  </span>
);

const inputCls =
  "w-full rounded-xl border border-black/10 bg-[var(--bg-base)] px-4 py-3 text-[15px] text-[var(--ink)] outline-none transition-colors focus:border-[var(--ember)] placeholder:text-[var(--muted)]/60";

const why = [
  { t: "Real impact", d: "Your work runs live businesses, not slide decks." },
  {
    t: "You power the characters",
    d: "Every agent is operated by a person. That person could be you.",
  },
  {
    t: "The best tools, no gatekeeping",
    d: "We run the frontier stack and expect you to push it.",
  },
  {
    t: "Built to grow",
    d: "An operator model that scales — and takes you with it.",
  },
];

const teamsHiring = [
  {
    name: "Marketing & Creative",
    to: "/growth",
    d: "Content & creative, channels & campaigns, customer-facing AI agents, follow-up systems, reputation — the front office that turns attention into customers.",
  },
  {
    name: "Workflow & Automation",
    to: "/workflow",
    d: "Workflow design, automation, tool integration, operations optimization, AI agent deployment.",
  },
  {
    name: "Product & Engineering",
    to: "/product",
    d: "Product strategy, full-stack engineering, applied AI, custom LLM and data work.",
  },
  {
    name: "AI Standards",
    to: null,
    d: "The human oversight layer: quality, compliance, and judgment above both teams.",
  },
];

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function Careers() {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    team: "",
    portfolio: "",
    why: "",
  });
  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const er = {};
    if (!form.fullName.trim()) er.fullName = "Please enter your name.";
    if (!emailOk(form.email)) er.email = "Please enter a valid email.";
    if (!form.team) er.team = "Please choose a team.";
    if (!form.why.trim()) er.why = "Tell us a little about you.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      if (FORM_ENDPOINT) {
        const data = new FormData();
        Object.entries(form).forEach(([k, v]) => data.append(k, v));
        if (resume) data.append("resume", resume);
        await fetch(FORM_ENDPOINT, { method: "POST", body: data });
      }
      setStatus("success");
    } catch {
      setStatus("success"); // graceful: still confirm to the applicant
    }
  };

  return (
    <>
      <Hero
        image={POSTER.careers}
        video={VIDEO.careers}
        eyebrow="CAREERS · WE'RE HIRING OPERATORS"
        title="Be the human behind the AI."
        subline="We're building the team that runs AI departments for real businesses. If you're the best at what you do and you refuse to ship slop, you'll fit right in."
        primary={{ label: "Apply to join", href: "#apply" }}
        secondary={{ label: "See the teams →", href: "#teams-hiring" }}
      />

      {/* Why work here */}
      <section className="bg-[var(--bg-base)] py-28 md:py-36" data-testid="careers-why">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Label>Why work here</Label></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-7 max-w-2xl t-section">
              The kind of place the best operators stay.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {why.map((w, i) => (
              <Reveal key={w.t} delay={(i % 4) * 80}>
                <div className="h-full rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--ember)]/30 hover:shadow-[0_24px_50px_-28px_rgba(20,18,16,0.4)]">
                  <h3 className="text-lg font-semibold tracking-tight">{w.t}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[var(--muted)]">
                    {w.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Teams hiring */}
      <section id="teams-hiring" className="bg-[var(--bg-contrast)] py-28 md:py-36" data-testid="careers-teams">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Label>Teams hiring</Label></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-7 max-w-2xl t-section">
              Find the team you'd run inside.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {teamsHiring.map((t, i) => {
              const Inner = (
                <div className="group flex h-full items-start justify-between gap-6 rounded-2xl border border-black/5 bg-[var(--bg-base)] p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--ember)]/30 hover:shadow-[0_24px_50px_-28px_rgba(20,18,16,0.4)]">
                  <div>
                    <h3 className="text-xl font-semibold tracking-tight">
                      {t.name}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-[var(--muted)]">
                      {t.d}
                    </p>
                  </div>
                  {t.to && (
                    <ArrowRight
                      size={20}
                      className="mt-1 flex-shrink-0 text-[var(--ember)] transition-transform duration-300 group-hover:translate-x-1"
                    />
                  )}
                </div>
              );
              return (
                <Reveal key={t.name} delay={(i % 2) * 80}>
                  {t.to ? (
                    <Link to={t.to} data-testid={`careers-team-${t.to.slice(1)}`}>
                      {Inner}
                    </Link>
                  ) : (
                    Inner
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="bg-[var(--bg-base)] py-28 md:py-36" data-testid="careers-apply">
        <div className="mx-auto max-w-2xl px-6 md:px-12">
          <Reveal><Label>Apply</Label></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-7 t-section">
              Apply to join the department.
            </h2>
          </Reveal>

          {status === "success" ? (
            <Reveal>
              <div
                className="mt-10 flex items-start gap-4 rounded-2xl border border-[var(--ember)]/30 bg-[var(--ember)]/[0.06] p-8"
                data-testid="careers-success"
              >
                <CheckCircle2 className="mt-0.5 flex-shrink-0 text-[var(--ember)]" size={24} />
                <div>
                  <p className="text-lg font-semibold">Application received.</p>
                  <p className="mt-2 text-[15px] leading-relaxed text-[var(--muted)]">
                    Thanks — we read every one. If there's a fit, a real person
                    will reach out.
                  </p>
                </div>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={140}>
              <form className="mt-10 space-y-5" onSubmit={onSubmit} noValidate data-testid="careers-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium" htmlFor="fullName">
                      Full name
                    </label>
                    <input
                      id="fullName"
                      className={inputCls}
                      value={form.fullName}
                      onChange={set("fullName")}
                      data-testid="careers-name"
                    />
                    {errors.fullName && (
                      <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.fullName}</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium" htmlFor="email">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      className={inputCls}
                      value={form.email}
                      onChange={set("email")}
                      data-testid="careers-email"
                    />
                    {errors.email && (
                      <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium" htmlFor="phone">
                      Phone
                    </label>
                    <input
                      id="phone"
                      className={inputCls}
                      value={form.phone}
                      onChange={set("phone")}
                      data-testid="careers-phone"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium" htmlFor="team">
                      Which team?
                    </label>
                    <select
                      id="team"
                      className={inputCls}
                      value={form.team}
                      onChange={set("team")}
                      data-testid="careers-team-select"
                    >
                      <option value="">Select a team</option>
                      <option>Marketing &amp; Creative</option>
                      <option>Workflow &amp; Automation</option>
                      <option>Product &amp; Engineering</option>
                      <option>AI Standards</option>
                      <option>Not sure</option>
                    </select>
                    {errors.team && (
                      <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.team}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium" htmlFor="portfolio">
                    Portfolio or LinkedIn URL
                  </label>
                  <input
                    id="portfolio"
                    className={inputCls}
                    placeholder="https://"
                    value={form.portfolio}
                    onChange={set("portfolio")}
                    data-testid="careers-portfolio"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium" htmlFor="resume">
                    Resume
                  </label>
                  <input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="w-full text-[14px] text-[var(--muted)] file:mr-4 file:rounded-full file:border-0 file:bg-[var(--ember)]/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-[var(--ember)] hover:file:bg-[var(--ember)]/20"
                    onChange={(e) => setResume(e.target.files?.[0] || null)}
                    data-testid="careers-resume"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium" htmlFor="whyyou">
                    Why you?
                  </label>
                  <textarea
                    id="whyyou"
                    rows={5}
                    className={inputCls}
                    value={form.why}
                    onChange={set("why")}
                    data-testid="careers-whyyou"
                  />
                  {errors.why && (
                    <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.why}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  disabled={status === "submitting"}
                  data-testid="careers-submit"
                >
                  {status === "submitting" ? "Sending…" : "Apply to join"}
                </Button>
              </form>
            </Reveal>
          )}

          <Reveal delay={120}>
            <p className="mt-10 text-[15px] text-[var(--muted)]">
              Don't see your role?{" "}
              <a href="#apply" className="font-semibold text-[var(--ember)] hover:text-[var(--ember-deep)]">
                Tell us what you'd run.
              </a>
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
